import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Button, Form, Spinner, Image, Badge, Alert, Modal } from 'react-bootstrap';
import { useGlobal } from '../../contexts/GlobalContext';
import { PaymentGateway } from '../../gateways/PaymentGateway';
import { getSiteKey } from '../../firebaseConfig';
import PasswordInput from '../common/PasswordInput';
import { Utils } from '../../common/Utils';

interface ProviderStatus {
    yoco: boolean;
    snapscan: boolean;
    zapper: boolean;
}

const PaymentProviders: React.FC = () => {
    const [status, setStatus] = useState<ProviderStatus>({
        yoco: false,
        snapscan: false,
        zapper: false,
    });

    const [yocoKey, setYocoKey] = useState('');
    const [zapperApiKey, setZapperApiKey] = useState('');
    const [zapperMerchantSiteApiKey, setZapperMerchantSiteApiKey] = useState('');
    const [zapperMerchantId, setZapperMerchantId] = useState('');
    const [zapperMerchantSiteId, setZapperMerchantSiteId] = useState('');
    const [emailCode, setEmailCode] = useState('');
    const [snapCode, setSnapCode] = useState('');
    const [snapApiKey, setSnapApiKey] = useState('');
    const [snapWebhookAuthKey, setSnapWebhookAuthKey] = useState('');
    const [loadingYoco, setLoadingYoco] = useState(false);
    const [loadingSnap, setLoadingSnap] = useState(false);
    const [loadingZapper, setLoadingZapper] = useState(false);
    const yocoErrorDefault = { secret: '', error: '' };
    const [yocoError, setYocoError] = useState(yocoErrorDefault);
    const zapperErrorDefault = { apiKey: '', merchantId: '', merchantSiteId: '', merchantSiteApiKey: '', error: '' };
    const [zapperError, setZapperError] = useState(zapperErrorDefault);
    const snapscanErrorDefault = { apiKey: '', snapCode: '', webhookAuthKey: '', error: '' };
    const [snapscanError, setSnapError] = useState(snapscanErrorDefault);
    const [showEmailBody, setShowEmailBody] = useState(false);
    const { settings, user } = useGlobal();

    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const handleOpen = (image: string) => setSelectedImage(image);
    const handleClose = () => setSelectedImage(null);

    const generateSnapScanEmailBody = (code: string) => {
        return `Hi SnapScan team,

Please add the following webhook to our account and send me the API key and Webhook Auth Key.

Webhook URL: https://orderq.co.za/api/snapscanWebhook/${getSiteKey()}
SnapCode: ${code}

Kind regards,
${settings?.siteTitle}`
    }
    const generateZapperEmailBody = (code: string) => {
        return `Hi Zapper team,

Please add the following webhook to our account:

Webhook URL: https://orderq.co.za/api/zapperWebhook/${getSiteKey()}
Site name: ${code}

Kind regards,
${settings?.siteTitle}`
    }
    const [emailBody, setEmailBody] = useState("");
    const [emailPaymentType, setEmailPaymentType] = useState<'SnapScan' | 'Zapper'>('SnapScan');

    useEffect(() => {
        if (settings && settings.checkoutPaymentOptionsEnabled) {
            const isYocoEnabled = settings.checkoutPaymentOptionsEnabled.includes("Yoco");
            const isSnapScanEnabled = settings.checkoutPaymentOptionsEnabled.includes("SnapScan");
            const isZapperEnabled = settings.checkoutPaymentOptionsEnabled.includes("Zapper");

            setStatus({
                yoco: isYocoEnabled,
                snapscan: isSnapScanEnabled,
                zapper: isZapperEnabled
            });
        }

    }, [settings]);

    const deleteSettingCache = async () => {
        const key = getSiteKey();
        const storageKey = `orderq-${key}`;
        sessionStorage.removeItem(storageKey);
    }

    const activateYoco = async () => {
        setYocoError(yocoErrorDefault);
        let hasError = false;
        if (yocoKey.trim() === '') {
            setYocoError((prev) => ({ ...prev, secret: 'Secret key is required.' }));
            hasError = true;
        }
        if (hasError)
            return;

        setLoadingYoco(true);
        try {
            if (!user) throw new Error('User not authenticated');

            const idToken = await user.getIdToken();
            const response = await PaymentGateway.YocoRegister(yocoKey, idToken);
            if (response.ok) {
                setStatus(prev => ({ ...prev, yoco: true }));
                deleteSettingCache();
            } else {
                const errorText = await response.text();
                setYocoError((prev) => ({ ...prev, error: `Activation failed: ${errorText}` }));
            }

        } catch (error: any) {
            console.error('Yoco activation failed:', error);
            setYocoError((prev) => ({ ...prev, error: `Activation failed: ${error.message}` }));
        } finally {
            setLoadingYoco(false);
        }
    };

    const activateZapper = async () => {
        setZapperError(zapperErrorDefault);
        let hasError = false;
        if (zapperApiKey.trim() === '') {
            setZapperError((prev) => ({ ...prev, apiKey: 'API key is required.' }));
            hasError = true;
        }
        if (zapperMerchantSiteApiKey.trim() === '') {
            setZapperError((prev) => ({ ...prev, merchantSiteApiKey: 'Merchant Site API key is required.' }));
            hasError = true;
        }
        if (zapperMerchantId.trim() === '') {
            setZapperError((prev) => ({ ...prev, merchantId: 'Merchant ID is required.' }));
            hasError = true;
        }
        if (zapperMerchantSiteId.trim() === '') {
            setZapperError((prev) => ({ ...prev, merchantSiteId: 'Merchant Site ID is required.' }));
            hasError = true;
        }
        if (hasError)
            return;

        setLoadingZapper(true);
        try {
            if (!user) throw new Error('User not authenticated');

            const idToken = await user.getIdToken();
            const response = await PaymentGateway.ZapperRegister(zapperMerchantId, zapperMerchantSiteId, zapperMerchantSiteApiKey, zapperApiKey, idToken);
            if (response.ok) {
                setStatus(prev => ({ ...prev, zapper: true }));
                deleteSettingCache();
            } else {
                const errorText = await response.text();
                setZapperError((prev) => ({ ...prev, error: `Activation failed: ${errorText}` }));
            }

        } catch (error: any) {
            console.error('Zapper activation failed:', error);
            setZapperError((prev) => ({ ...prev, error: `Activation failed: ${error.message}` }));
        } finally {
            setLoadingZapper(false);
        }
    };

    const activateSnapScan = async () => {
        setSnapError(snapscanErrorDefault);
        let hasError = false;
        if (snapApiKey.trim() === '') {
            setSnapError((prev) => ({ ...prev, apiKey: 'API key is required.' }));
            hasError = true;
        }
        if (snapCode.trim() === '') {
            setSnapError((prev) => ({ ...prev, snapCode: 'Snap Code is required.' }));
            hasError = true;
        }
        if (snapWebhookAuthKey.trim() === '') {
            setSnapError((prev) => ({ ...prev, webhookAuthKey: 'Webhook Auth Key is required.' }));
            hasError = true;
        }
        if (hasError)
            return;

        setLoadingSnap(true);
        try {
            if (!user) throw new Error('User not authenticated');

            const idToken = await user.getIdToken();
            const response = await PaymentGateway.SnapScanRegister(snapCode, snapApiKey, snapWebhookAuthKey, idToken);
            if (response.ok) {
                setStatus(prev => ({ ...prev, snapscan: true }));
                deleteSettingCache();
            } else {
                const errorText = await response.text();
                setSnapError((prev) => ({ ...prev, error: `Activation failed: ${errorText}` }));
            }

        } catch (error: any) {
            console.error('SnapScan activation failed:', error);
            setSnapError((prev) => ({ ...prev, error: `Activation failed: ${error.message}` }));
        } finally {
            setLoadingSnap(false);
        }
    };

    const renderStatus = (active: boolean) => (
        <Badge bg={active ? 'success' : 'secondary'}>
            {active ? 'Activated' : 'Deactivated'}
        </Badge>
    );

    return (
        <Container className="mt-4">
            <h2>Payment Options</h2>
            <Row className="gy-4">
                {/* Yoco */}
                <Col md={4}>
                    <Card style={{ backgroundColor: status.yoco ? "#f3f9f6" : "#ffffff" }}>
                        <Card.Body>
                            <Card.Title className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Image src="/images/icons/yoco.png" height={50} className="me-2" />
                                    Yoco
                                </div>
                                {renderStatus(status.yoco)}
                            </Card.Title>
                            <hr />
                            <p>OrderQ provides a secure connection to Yoco, allowing you to easily link your Yoco account and receive online card payments directly into your wallet.</p>

                            <p>Follow these simple steps to integrate with Yoco:</p>
                            <ol>
                                <li>Navigate to your <a target='_blank' rel="noreferrer" href="https://app.yoco.com/sales/payments/payment-gateway">Yoco Payment Gateway</a> page.</li>
                                <li>Log in using your Yoco username and password, if prompted.</li>
                                <li>Copy your <b>Live secret key</b> by clicking the copy icon, or click the eye icon to view and manually copy it.</li>
                                <li>Paste the secret key into the textbox below, then click <b>Activate</b> or <b>Update</b>.</li>
                                <li>To finally accept payments, navigate to your <a href={Utils.GetUrl("/admin/settings")}>Settings</a> page and select Yoco as an <b>Available Payment Options at Checkout</b></li>
                            </ol>

                            <span style={{ fontStyle: 'italic' }}>
                                Please note: You can copy and test using the <b>Test secret key</b>, but remember to update to the <b>Live secret key</b> to receive payments.
                            </span>

                            <div className="mb-2">
                                <Image
                                    src="/yoco-integration.png"
                                    thumbnail
                                    style={{ maxWidth: '100px', cursor: 'pointer' }}
                                    onClick={() => handleOpen("/yoco-integration.png")}
                                    alt="Click to enlarge"
                                />
                            </div>                              

                            <PasswordInput
                                value={yocoKey}
                                onChange={e => setYocoKey(e)}
                                placeholder="Enter Secret Key"
                                isInvalid={!!yocoError.secret}
                                label='Secret Key'
                                feedback={yocoError.secret}
                            />
                            {yocoError.error && <Alert style={{ marginTop: "20px" }} variant="danger">{yocoError.error}</Alert>}
                            <Button
                                variant="primary"
                                className="mt-3"
                                onClick={activateYoco}
                                disabled={loadingYoco}
                            >
                                {loadingYoco ? <Spinner animation="border" size="sm" /> : status.yoco ? 'Update' : 'Activate'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* SnapScan */}
                <Col md={4}>
                    <Card style={{ backgroundColor: status.snapscan ? "#f3f9f6" : "#ffffff" }}>
                        <Card.Body>
                            <Card.Title className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Image src="/images/icons/snapscan.png" height={50} className="me-2" />
                                    SnapScan
                                </div>
                                {renderStatus(status.snapscan)}
                            </Card.Title>

                            <hr />
                            <p>OrderQ provides a secure connection to SnapScan, allowing you to easily link your SnapScan account and receive online card payments directly into your wallet.</p>

                            <p>Follow these simple steps to integrate with SnapScan:</p>

                            <ol>
                                <li>Navigate to your SnapScan <a href="https://merchant.getsnapscan.com/payment_methods/qr_codes" target="_blank" rel="noreferrer">QR Code</a> page (Payment Methods → QR Codes), and copy your <b>Snap Code</b> (your QR code value).</li>
                                <li>Click on the following button to generate an email to SnapScan using your Snap Code: <Button onClick={() => {
                                    setEmailPaymentType('SnapScan');
                                    setEmailBody(generateSnapScanEmailBody(snapCode));
                                    setShowEmailBody(true)
                                }}>Generate Email</Button></li>
                                <li>Email SnapScan at <a href="mailto:help@snapscan.co.za?subject=SnapScan%20Webhook">help@snapscan.co.za</a> with the email body generated in the above step, requesting that they add our webhook to your account.</li>
                                <li>Once they reply, copy the <b>API key</b> and <b>Webhook Auth Key</b> from their email, along with your <b>Snapcode</b>, into the fields below and click <b>Activate</b> or <b>Update</b>.</li>
                                <li>To finally accept payments, navigate to your <a href={Utils.GetUrl("/admin/settings")}>Settings</a> page and select SnapScan as an <b>Available Payment Options at Checkout</b></li>
                            </ol>
                            <div className="mb-2">
                                <Image
                                    src="/snapscan-integration.png"
                                    thumbnail
                                    style={{ maxWidth: '100px', cursor: 'pointer' }}
                                    onClick={() => handleOpen("/snapscan-integration.png")}
                                    alt="Click to enlarge"
                                />
                            </div>                            
                            <Form.Group controlId="snapCode">
                                <Form.Label>Snap Code</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={snapCode}
                                    onChange={e => setSnapCode(e.target.value)}
                                    placeholder="Enter Snap Code"
                                    isInvalid={!!snapscanError.snapCode}
                                />
                                <Form.Control.Feedback type="invalid">{snapscanError.snapCode}</Form.Control.Feedback>
                            </Form.Group>
                            <PasswordInput
                                value={snapApiKey}
                                onChange={e => setSnapApiKey(e)}
                                placeholder="Enter API Key"
                                isInvalid={!!snapscanError.apiKey}
                                label='API Key'
                                feedback={snapscanError.apiKey}
                            />
                            <PasswordInput
                                value={snapWebhookAuthKey}
                                onChange={e => setSnapWebhookAuthKey(e)}
                                placeholder="Enter Webhook Auth Key"
                                isInvalid={!!snapscanError.webhookAuthKey}
                                label='Webhook Auth Key'
                                feedback={snapscanError.webhookAuthKey}
                            />
                            {snapscanError.error && <Alert style={{ marginTop: "20px" }} variant="danger">{snapscanError.error}</Alert>}
                            <Button
                                variant="primary"
                                className="mt-3"
                                onClick={activateSnapScan}
                                disabled={loadingSnap}
                            >
                                {loadingSnap ? <Spinner animation="border" size="sm" /> : status.snapscan ? 'Update' : 'Activate'}
                            </Button>
                            <div className="mt-4">
                                <Alert variant="warning">
                                    <strong>Warning:</strong> SnapScan allows users to make partial payments. We will accept the payment and set the order status to <b>New</b>. A warning will be displayed on the order screen, and it is the store’s responsibility to collect the full payment.
                                </Alert>
                            </div>

                        </Card.Body>
                    </Card>
                </Col>

                {/* Zapper */}
                <Col md={4}>
                    <Card>
                        <Card.Body style={{ backgroundColor: status.zapper ? "#f3f9f6" : "#ffffff" }}>
                            <Card.Title className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Image src="/images/icons/zapper.png" height={50} className="me-2" />
                                    Zapper
                                </div>
                                {renderStatus(status.zapper)}
                            </Card.Title>
                            <hr />
                            <p>
                                OrderQ provides a secure connection to Zapper, allowing you to easily link your Zapper account and receive online card payments directly into your wallet.
                            </p>
                            <p>Follow these simple steps to integrate with Zapper:</p>
                            <ol>
                                <li>Navigate to your <a href="https://www.zapper.com/mp" target="_blank" rel="noreferrer">Zapper Merchant Portal</a>.</li>
                                <li>Log in using your Zapper username and password, if prompted.</li>
                                <li>Open your integrations page (under <b>Settings → Integrations</b>) and copy your <b>Merchant API Key</b> and the <b>Merchant Site API Key</b> by clicking the copy icons, and paste it into the correct textboxes below.</li>
                                <li>Click <b>Integration Information - Version 1</b>, then copy the <b>Merchant ID</b> and <b>Site ID</b> values and paste them into the relevant textboxes below.</li>
                                <li>Click <b>Activate</b> or <b>Update</b> to complete the integration.</li>
                                <li>To finally accept payments, navigate to your <a href={Utils.GetUrl("/admin/settings")}>Settings</a> page and select Zapper as an <b>Available Payment Options at Checkout</b></li>
                            </ol>

                            <div className="mb-2">
                                <Image
                                    src="/zapper-integration.png"
                                    thumbnail
                                    style={{ maxWidth: '100px', cursor: 'pointer' }}
                                    onClick={() => handleOpen("/zapper-integration.png")}
                                    alt="Click to enlarge"
                                />
                            </div>

                            <PasswordInput
                                value={zapperApiKey}
                                onChange={e => setZapperApiKey(e)}
                                placeholder="Enter Merchant API Key"
                                isInvalid={!!zapperError.apiKey}
                                label='Merchant API Key'
                                feedback={zapperError.apiKey}
                            />
                            <PasswordInput
                                value={zapperMerchantSiteApiKey}
                                onChange={e => setZapperMerchantSiteApiKey(e)}
                                placeholder="Enter Merchant Site API Key"
                                isInvalid={!!zapperError.merchantSiteApiKey}
                                label='Merchant Site API Key'
                                feedback={zapperError.merchantSiteApiKey}
                            />
                            <Form.Group controlId="zapperMerchantId">
                                <Form.Label>Merchant ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={zapperMerchantId}
                                    onChange={e => setZapperMerchantId(e.target.value)}
                                    placeholder="Enter Merchant ID"
                                    isInvalid={!!zapperError.merchantId}
                                />
                                <Form.Control.Feedback type="invalid">{zapperError.merchantId}</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId="zapperMerchantSiteId">
                                <Form.Label>Site ID</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={zapperMerchantSiteId}
                                    onChange={e => setZapperMerchantSiteId(e.target.value)}
                                    placeholder="Enter Merchant Site ID"
                                    isInvalid={!!zapperError.merchantSiteId}
                                />
                                <Form.Control.Feedback type="invalid">{zapperError.merchantSiteId}</Form.Control.Feedback>
                            </Form.Group>
                            {zapperError.error && <Alert style={{ marginTop: "20px" }} variant="danger">{zapperError.error}</Alert>}
                            <Button
                                variant="primary"
                                className="mt-3"
                                onClick={activateZapper}
                                disabled={loadingZapper}
                            >
                                {loadingZapper ? <Spinner animation="border" size="sm" /> : status.zapper ? 'Update' : 'Activate'}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={showEmailBody} centered onHide={() => { setShowEmailBody(false) }}>
                <Modal.Header closeButton>
                    <Modal.Title>Generate {emailPaymentType} Email</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form.Group controlId="codeInput">
                        <Form.Label>Enter your {emailPaymentType === 'SnapScan' ? 'Snap Code' : 'Site Name'}</Form.Label>
                        <Form.Control
                            type="text"
                            value={emailCode}
                            onChange={(e) => {
                                setEmailBody(emailPaymentType === 'SnapScan' ? generateSnapScanEmailBody(e.target.value) : generateZapperEmailBody(e.target.value));
                                setEmailCode(e.target.value)
                            }}
                            placeholder={`e.g. ${emailPaymentType === 'SnapScan' ? 'gial0SNH' : settings.siteTitle?.replace(/\s+/g, '')}`}
                        />
                    </Form.Group>

                    <Form.Group controlId="emailPreview" className="mt-3">
                        <Form.Label>Email Body Preview</Form.Label>
                        <Form.Control as="textarea" rows={6} value={emailBody} readOnly />
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowEmailBody(false) }}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => {
                            Utils.CopyToClipboard(emailBody);
                            setShowEmailBody(false);
                        }}
                    >
                        Copy & Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!selectedImage} onHide={handleClose} centered size="lg">

                <Modal.Header closeButton>                    
                </Modal.Header>

                <Modal.Body className="text-center">
                    {selectedImage && (
                        <Image
                            src={selectedImage}
                            fluid
                        />
                    )}
                </Modal.Body>
            </Modal>

        </Container>
    );
};

export default PaymentProviders;